import { ContactConfig, ContactProps } from '@/models/Contact';
import { ProgramNames } from '@/models/Program';
import { USCountryId } from '@/utils/constants';
import { phoneNumberIsValid } from '@/utils/contactUtils';
import { isEmail } from '@/utils/emailUtil';
import { stringHasValue } from '@/utils/stringUtil';

export type MissingAddressField =
  | 'streetAddress'
  | 'city'
  | 'state'
  | 'zipCode'
  | 'county'
  | 'country';

export interface AddressValidationResult {
  missingFields: MissingAddressField[];
  hasInvalidZipCode: boolean;
}

function isUnitedStatesContact(
  programId: ProgramNames,
  countryId: ContactProps['countryId']
): boolean {
  return programId !== ProgramNames.TreeCitiesWorld || countryId === USCountryId;
}

export function isZipInValid(
  zip: string | null | undefined,
  countryId: number | null | undefined,
  programId: ProgramNames
): boolean {
  if (!stringHasValue(zip) || countryId == null) {
    return true;
  }

  const regex = /^\d{5}(-\d{4})?$/;
  if (programId !== ProgramNames.TreeCitiesWorld || countryId === USCountryId) {
    return !regex.test(zip);
  }

  return zip.toString().length > 10;
}

export function getMissingAddressFields(
  contact: ContactProps,
  programId: ProgramNames
): MissingAddressField[] {
  const missingFields: MissingAddressField[] = [];
  const usContact = isUnitedStatesContact(programId, contact.countryId);

  if (!stringHasValue(contact.streetAddress1)) {
    missingFields.push('streetAddress');
  }
  if (!stringHasValue(contact.city)) {
    missingFields.push('city');
  }
  if (!stringHasValue(contact.zipCode)) {
    missingFields.push('zipCode');
  }
  if (usContact && !stringHasValue(contact.stateId)) {
    missingFields.push('state');
  }
  if (usContact && !stringHasValue(contact.countyId)) {
    missingFields.push('county');
  }
  if (contact.countryId == null) {
    missingFields.push('country');
  }

  return missingFields;
}

export function getAddressValidation(
  contact: ContactProps,
  programId: ProgramNames
): AddressValidationResult {
  const missingFields = getMissingAddressFields(contact, programId);
  const hasInvalidZipCode = isZipInValid(contact.zipCode, contact.countryId, programId);

  return {
    missingFields,
    hasInvalidZipCode,
  };
}

export function getContactHasError(
  contact: ContactProps,
  contactConfig: ContactConfig,
  programId: ProgramNames
): boolean {
  const status: boolean[] = [];

  status.push(!stringHasValue(contact.firstName));
  status.push(!stringHasValue(contact.lastName));

  if (contactConfig.displayPhone) {
    status.push(!stringHasValue(contact.phone) || !phoneNumberIsValid(contact.phone));
  }
  if (contactConfig.displayEmail) {
    status.push(!stringHasValue(contact.email) || !isEmail(contact.email));
  }
  if (contactConfig.displayDepartment) {
    status.push(contact.departmentTypeId == null);
  }
  if (contactConfig.displayTitle) {
    status.push(!stringHasValue(contact.title));
  }
  if (contactConfig.displayAddress) {
    const addressValidation = getAddressValidation(contact, programId);
    status.push(addressValidation.missingFields.length > 0);
    status.push(addressValidation.hasInvalidZipCode);
  }

  return status.some((errorExists) => errorExists);
}
